import { LorebookEntryOpType } from "@next-platform/packages-ts-types";
/**
 * Per-row marker for how a staged edit affects an entry in the list, used to badge the row.
 * `undefined` (no marker on the row) means the row is unchanged from live data.
 */
export var StagedEntryState;
(function (StagedEntryState) {
    StagedEntryState["NEW"] = "new";
    StagedEntryState["EDITED"] = "edited";
    StagedEntryState["REMOVED"] = "removed";
})(StagedEntryState || (StagedEntryState = {}));
/**
 * Build a synthetic list entry for a staged create so it can render in the list before it exists on
 * the backend. Only the user-visible fields are populated; the temp id keys the row and lets the
 * edit modal re-open the same staged op.
 */
function buildStagedCreateEntry(op) {
    return {
        id: op.entry_id,
        name: op.data?.name ?? "",
        keywords: op.data?.keywords ?? [],
        content: op.data?.content ?? "",
    };
}
/** Does a staged create match the active server search? Mirrors the backend's name/keyword/content match. */
function matchesSearch(entry, search) {
    const query = search.trim().toLowerCase();
    if (!query) {
        return true;
    }
    return (entry.name.toLowerCase().includes(query) ||
        entry.content.toLowerCase().includes(query) ||
        entry.keywords.some((keyword) => keyword.toLowerCase().includes(query)));
}
/**
 * Overlay the aggregated staged ops onto the live entries for display. Shared by web and native.
 *
 * - Staged creates (`op: "create"`) become synthetic rows pinned at the top, badged NEW.
 * - Staged updates (`op: "update"`) override the live entry's edited fields, badged EDITED.
 * - Staged deletes (`op: "delete"`) keep the row visible but badged REMOVED (struck through in the UI).
 *
 * Updates/deletes targeting an entry that is not currently loaded are simply not visible — they still
 * reach the backend via the submitted snapshot, and the badge appears once the entry is paged in.
 * Both platforms page the edit list server-side, so this applies to each of them.
 *
 * `search` matters where the live rows are filtered server-side (both platforms' edit screens):
 * staged creates are then filtered client-side to match, so unrelated NEW rows do not pin themselves
 * to the top of a result set they do not belong to. The native create/import flow filters the merged
 * list downstream and omits the argument. Counts are unaffected either way: `effectiveTotal`
 * describes the whole lorebook, not the view.
 *
 * `effectiveTotal` is the live total plus staged creates minus staged deletes: the count the user is
 * proposing, kept consistent with the badged list. A staged delete always targets a live entry (a
 * create+delete cancels out during aggregation and never appears here), so each delete subtracts one.
 */
export function buildStagedEntryOverlay(entries, stagedOpByEntryId, liveTotal = entries.length, search = "") {
    if (!stagedOpByEntryId || stagedOpByEntryId.size === 0) {
        return { rows: entries, effectiveTotal: liveTotal };
    }
    const createRows = [];
    let createdCount = 0;
    let deletedCount = 0;
    for (const op of stagedOpByEntryId.values()) {
        if (op.op === LorebookEntryOpType.DELETE) {
            deletedCount += 1;
            continue;
        }
        if (op.op !== LorebookEntryOpType.CREATE) {
            continue;
        }
        // Counted regardless of the filter: the create still exists outside the current search view.
        createdCount += 1;
        const entry = buildStagedCreateEntry(op);
        if (matchesSearch(entry, search)) {
            createRows.push({ ...entry, stagedState: StagedEntryState.NEW });
        }
    }
    const liveRows = entries.map((entry) => {
        const op = stagedOpByEntryId.get(entry.id);
        if (!op) {
            return entry;
        }
        if (op.op === LorebookEntryOpType.DELETE) {
            return { ...entry, stagedState: StagedEntryState.REMOVED };
        }
        if (op.op === LorebookEntryOpType.UPDATE) {
            // Apply only the staged fields over the live entry so unedited fields still render.
            return { ...entry, ...op.data, stagedState: StagedEntryState.EDITED };
        }
        return entry;
    });
    return {
        rows: [...createRows, ...liveRows],
        effectiveTotal: liveTotal + createdCount - deletedCount,
    };
}
/**
 * Split a list into staged creates and everything else, so the creates can be pinned above the
 * sorted live entries.
 *
 * A staged create exists only client-side and carries no timestamp, so any time-based sort would
 * treat it as epoch-zero and sink a just-added entry to the bottom of the list — out of view right
 * after the user added it. Both platforms pin for this reason.
 */
export function pinStagedCreates(entries) {
    const stagedCreates = [];
    const liveEntries = [];
    for (const entry of entries) {
        if (entry.stagedState === StagedEntryState.NEW) {
            stagedCreates.push(entry);
        }
        else {
            liveEntries.push(entry);
        }
    }
    return { stagedCreates, liveEntries };
}
